import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import type { BillingInterval, RegistrySubscription, RegistrySubscriptionsResponse } from "@/types/api";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDate, formatMoney } from "@/lib/billing-format";
import { cn } from "@/lib/utils";
import { StatCard } from "./stat-card";

const INTERVAL_MONTHS: Record<BillingInterval, number> = {
  "1 month": 1,
  "3 months": 3,
  "6 months": 6,
  "12 months": 12,
};

function statusVariant(status: string | null): "success" | "secondary" | "muted" | "destructive" {
  if (status === "active") return "success";
  if (status === "past_due" || status === "failed") return "destructive";
  if (status === "canceled" || status === "paused") return "muted";
  return "secondary";
}

function SubscriptionRow({ row }: { row: RegistrySubscription }) {
  const { t } = useTranslation("billing");
  return (
    <TableRow>
      <TableCell>
        <Link
          to={`/instances/${row.instanceName}`}
          className="font-mono text-xs text-primary hover:underline"
        >
          {row.instanceName}
        </Link>
      </TableCell>
      <TableCell className="whitespace-nowrap tabular-nums">
        {formatMoney(row.amountCents, row.currency)}
        {row.interval ? (
          <span className="block text-xs text-muted-foreground">
            {t("payments.subscriptions.interval", { count: INTERVAL_MONTHS[row.interval] })}
          </span>
        ) : null}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {row.collectionMethod ? t(`payments.subscriptions.collection.${row.collectionMethod}`) : "—"}
      </TableCell>
      <TableCell>
        {row.subscriptionStatus ? (
          <Badge variant={statusVariant(row.subscriptionStatus)}>{row.subscriptionStatus}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">
            {t("payments.subscriptions.noSubscription")}
          </span>
        )}
        {row.stripeRefMissing ? (
          <Badge
            variant="outline"
            className="mt-1 border-amber-400 text-amber-800 dark:text-amber-300"
            title={t("payments.subscriptions.stripeRefMissingHint")}
          >
            {t("payments.subscriptions.stripeRefMissing")}
          </Badge>
        ) : null}
        {row.cancelAtPeriodEnd ? (
          <span className="mt-1 block text-xs text-muted-foreground">
            {t("payments.subscriptions.cancelAtPeriodEnd")}
          </span>
        ) : null}
      </TableCell>
      <TableCell className="whitespace-nowrap text-muted-foreground">
        {formatDate(row.currentPeriodEnd)}
      </TableCell>
      <TableCell className="whitespace-nowrap text-muted-foreground">
        {formatDate(row.paidThrough)}
      </TableCell>
      <TableCell className="whitespace-nowrap text-right tabular-nums">
        {formatMoney(row.debtCents, row.currency)}
      </TableCell>
      <TableCell
        className={cn(
          "whitespace-nowrap text-right tabular-nums",
          row.overdueCents > 0 && "font-semibold text-destructive"
        )}
      >
        {formatMoney(row.overdueCents, row.currency)}
      </TableCell>
    </TableRow>
  );
}

export function SubscriptionsTab() {
  const { t } = useTranslation("billing");
  const { data, isLoading, error } = useQuery({
    queryKey: ["billing", "registry", "subscriptions"],
    queryFn: () => api.get<RegistrySubscriptionsResponse>("/api/admin/billing/subscriptions"),
  });

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (error) {
    return (
      <p className="text-sm text-destructive">
        {t("loadFailed", { message: error instanceof Error ? error.message : String(error) })}
      </p>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <StatCard
          title={t("payments.subscriptions.mrr")}
          value={formatMoney(data.summary.mrrCents, "EUR")}
          hint={t("payments.subscriptions.mrrHint")}
        />
        <StatCard
          title={t("payments.subscriptions.receivables")}
          value={formatMoney(data.summary.receivablesCents, "EUR")}
          hint={t("payments.subscriptions.receivablesHint")}
          tone={data.summary.receivablesCents > 0 ? "destructive" : "default"}
        />
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("payments.subscriptions.columns.instance")}</TableHead>
                  <TableHead>{t("payments.subscriptions.columns.amount")}</TableHead>
                  <TableHead>{t("payments.subscriptions.columns.collection")}</TableHead>
                  <TableHead>{t("payments.subscriptions.columns.status")}</TableHead>
                  <TableHead>{t("payments.subscriptions.columns.periodEnd")}</TableHead>
                  <TableHead>{t("payments.subscriptions.columns.paidThrough")}</TableHead>
                  <TableHead className="text-right">{t("payments.subscriptions.columns.debt")}</TableHead>
                  <TableHead className="text-right">{t("payments.subscriptions.columns.overdue")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                      {t("payments.subscriptions.empty")}
                    </TableCell>
                  </TableRow>
                ) : (
                  data.items.map((row) => <SubscriptionRow key={row.instanceName} row={row} />)
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
